import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import AlertMessage from '../common/AlertMessage';
import { Link } from 'react-router-dom';

const loginSchema = z.object({
  username: z.string(),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;
type LoginLocationState = {
  from?: { pathname?: string };
  registrationMessage?: string;
};

const ACCOUNT_VERIFIED_MESSAGE = 'Account activated successfully. You can now log in.';

const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as LoginLocationState | null) ?? null;
  const verifiedParam = new URLSearchParams(location.search).get('verified');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    locationState?.registrationMessage ?? (verifiedParam === '1' ? ACCOUNT_VERIFIED_MESSAGE : null)
  );
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const hasRegistrationMessage = Boolean(locationState?.registrationMessage);
    const hasVerifiedMessage = verifiedParam === '1';
    if (!hasRegistrationMessage && !hasVerifiedMessage) {
      return;
    }

    if (hasRegistrationMessage) {
      setSuccessMessage(locationState?.registrationMessage ?? null);
    } else if (hasVerifiedMessage) {
      setSuccessMessage(ACCOUNT_VERIFIED_MESSAGE);
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('verified');
    const preservedState = locationState?.from ? { from: locationState.from } : null;

    navigate({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    }, {
      replace: true,
      state: preservedState,
    });
  }, [location.pathname, location.search, locationState, navigate, verifiedParam]);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setError,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoginError(null);
      setResetMessage(null);
      setResetError(null);
      await login(data.username, data.password);

      // Redirect to the page they tried to visit or default to search
      const from = locationState?.from?.pathname || '/search';
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login failed', error);
      setLoginError(error instanceof Error ? error.message : 'Login failed. Please try again.');
    }
  };

  const handleForgotPassword = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setResetMessage(null);
    setResetError(null);

    const email = getValues('username')?.trim();
    if (!email) {
      setError('username', { type: 'required', message: 'Email is required to reset password' });
      return;
    }

    clearErrors('username');
    setIsResetting(true);
    try {
      const response = await apiService.resetPassword(email);
      setResetMessage(response?.message || 'Password reset request sent. Check your email.');
    } catch (error) {
      console.error('Reset password failed', error);
      setResetError(error instanceof Error ? error.message : 'Reset password failed. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl p-6 md:p-8 w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-white mb-6">Log In to Your Account</h2>
      
      {loginError && (
        <div className="mb-4">
          <AlertMessage variant="error">{loginError}</AlertMessage>
        </div>
      )}

      {successMessage && (
        <div className="mb-4">
          <AlertMessage variant="success">{successMessage}</AlertMessage>
        </div>
      )}

      {resetMessage && (
        <div className="mb-4">
          <AlertMessage variant="success">{resetMessage}</AlertMessage>
        </div>
      )}

      {resetError && (
        <div className="mb-4">
          <AlertMessage variant="error">{resetError}</AlertMessage>
        </div>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
            Email
          </label>
          <input
            id="username"
            type="username"
            {...register('username')}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
            placeholder="your.email@example.com"
          />
          {errors.username && <p className="mt-1 text-sm text-red-400">{errors.username.message}</p>}
        </div>
        
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
            Password
          </label>
          <input
            id="password"
            type="password"
            {...register('password')}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
            placeholder="********"
          />
          {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>}
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {/* <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 rounded bg-gray-700"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
              Remember me
            </label> */}
          </div>
          
          <div className="text-sm">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={isResetting}
              className="text-purple-400 hover:text-purple-300 disabled:text-purple-500/70"
            >
              Forgot your password?
            </button>
          </div>
        </div>
        
        <button
          type="submit"
          onClick={() => setSuccessMessage(null)}
          disabled={isSubmitting}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300 disabled:bg-purple-800 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Logging in...' : 'Log In'}
        </button>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-purple-400 hover:text-purple-300">
              Sign up
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
